import React, { useMemo, useState, useEffect } from 'react';
import { ReloadOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import type { ServiceEvent } from '../../../services/services.service';

const EVENTS_PER_PAGE_DEFAULT = 10;

export interface ServiceEventsListProps {
  events: ServiceEvent[];
  title?: string;
  emptyMessage?: string;
  refreshLabel?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  disabled?: boolean;
  eventsPerPage?: number; // 0 = show all, no pagination
}

const getEventIcon = (type: string) => {
  switch (type) {
    case 'success':
      return <CheckCircleOutlined className="text-green-600 dark:text-green-400" />;
    case 'error':
      return <CloseCircleOutlined className="text-red-600 dark:text-red-400" />;
    case 'warning':
      return <InfoCircleOutlined className="text-yellow-600 dark:text-yellow-400" />;
    default:
      return <ClockCircleOutlined className="text-blue-600 dark:text-blue-400" />;
  }
};

const getEventColor = (type: string): string => {
  switch (type) {
    case 'success':
      return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    case 'error':
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    case 'warning':
      return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
    default:
      return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
  }
};

const ServiceEventsList: React.FC<ServiceEventsListProps> = ({
  events,
  title,
  emptyMessage = 'No hay eventos disponibles',
  refreshLabel = 'Actualizar',
  onRefresh,
  isRefreshing = false,
  disabled = false,
  eventsPerPage = EVENTS_PER_PAGE_DEFAULT,
}) => {
  const [currentPage, setCurrentPage] = useState(1);

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()),
    [events]
  );

  const totalPages = eventsPerPage > 0 ? Math.ceil(sortedEvents.length / eventsPerPage) || 1 : 1;
  const paginatedEvents =
    eventsPerPage > 0
      ? sortedEvents.slice((currentPage - 1) * eventsPerPage, currentPage * eventsPerPage)
      : sortedEvents;

  useEffect(() => {
    setCurrentPage(1);
  }, [events.length]);

  return (
    <div className="space-y-3">
      {(title || onRefresh) && (
        <div className="flex items-center justify-between mb-4">
          {title && (
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
          )}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing || disabled}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={refreshLabel}
            >
              {isRefreshing ? (
                <LoadingOutlined spin />
              ) : (
                <ReloadOutlined />
              )}
              <span className="hidden sm:inline">{refreshLabel}</span>
            </button>
          )}
        </div>
      )}

      {events.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 mb-4">{emptyMessage}</p>
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing || disabled}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed mx-auto"
            >
              {isRefreshing ? (
                <>
                  <LoadingOutlined spin />
                  <span>Cargando...</span>
                </>
              ) : (
                <>
                  <ReloadOutlined />
                  <span>{refreshLabel}</span>
                </>
              )}
            </button>
          )}
        </div>
      ) : (
        <>
          {paginatedEvents.map((event) => (
            <div
              key={event.id}
              className={`border rounded-lg p-4 ${getEventColor(event.type)} ${
                event.isKubiyEvent ? 'ring-2 ring-purple-400 dark:ring-purple-500 shadow-md' : ''
              } ${
                event.isDeletionEvent
                  ? 'ring-2 ring-orange-400 dark:ring-orange-500 bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-600'
                  : ''
              }`}
            >
              <div className="flex items-start gap-3">
                <span className="flex-shrink-0 mt-0.5">{getEventIcon(event.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">{event.message}</p>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {event.timestamp.toLocaleString()}
                    </span>
                  </div>
                  {event.details && (
                    <p className="text-xs mt-1 text-gray-600 dark:text-gray-400 opacity-80">{event.details}</p>
                  )}
                </div>
              </div>
            </div>
          ))}

          {eventsPerPage > 0 && totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300 px-4">
                Página {currentPage} de {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ServiceEventsList;
